import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import {
  Activity,
  Beaker,
  Calendar,
  ChevronRight,
  FileText,
  MapPin,
  SparkleCircle,
  SparkleFile,
  Video2,
} from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { PulseLoader } from '../../../common/PulseLoader';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import LexicalReadOnlyMarkdownViewer from '@lifeomic/phc-web-toolkit/src/components/MarkdownEditor/Lexical/LexicalReadOnlyMarkdownViewer';
import urls from '../../../../util/urls';
import { memberDetailMessages } from './messages';
import { useMemberPulseScan } from '../../ZoriPulsePriorities/useMemberPulseScan';
import {
  useNextAppointment,
  type NextAppointmentSummary,
} from './useNextAppointment';
import { DocumentExtractionReviewStatus } from './DocumentExtractionReviewStatus';
import { ExtractionConfidenceBadge } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionConfidenceBadge';
import {
  getExtractedObservationValue,
  getExtractedResourceDate,
  useUnreviewedExtractedResources,
  type ReviewableResourceType,
  type UnreviewedExtractedResource,
} from './useUnreviewedExtractedResources';
import { ExtractedResourceReviewModal } from './ExtractedResourceReviewModal';
import { useExtractedResourceSourcePreload } from './useExtractedResourceSource';
import { CitationResourceModal } from './CitationResourceModal';
import { ActionToolkit } from './ActionToolkit';
import {
  parseChatMessageReference,
  parseCitationReference,
  type ParsedCitationReference,
} from './citationReference';

const messages = defineMessages({
  loading: {
    id: 'PrioritiesContent.loading',
    defaultMessage: 'Loading priorities…',
  },
  error: {
    id: 'PrioritiesContent.error',
    defaultMessage: 'Failed to load priorities for this member.',
  },
  empty: {
    id: 'PrioritiesContent.empty',
    defaultMessage: 'No priority items found for this member.',
  },
  nextAppointmentTitle: {
    id: 'PrioritiesContent.nextAppointmentTitle',
    defaultMessage: 'Next appointment',
  },
  lastAppointmentTitle: {
    id: 'PrioritiesContent.lastAppointmentTitle',
    defaultMessage: 'Last appointment',
  },
  noUpcomingAppointments: {
    id: 'PrioritiesContent.noUpcomingAppointments',
    defaultMessage: 'No upcoming appointments scheduled.',
  },
  practitionerLabel: {
    id: 'PrioritiesContent.practitionerLabel',
    defaultMessage: 'with {name}',
  },
  inPerson: {
    id: 'PrioritiesContent.inPerson',
    defaultMessage: 'In person',
  },
  virtual: {
    id: 'PrioritiesContent.virtual',
    defaultMessage: 'Virtual',
  },
  reviewTitle: {
    id: 'PrioritiesContent.reviewTitle',
    defaultMessage: 'Critical issues & things to review',
  },
  reviewSubtitle: {
    id: 'PrioritiesContent.reviewSubtitle',
    defaultMessage:
      '{count, plural, =0 {Nothing flagged} one {# item flagged} other {# items flagged}}',
  },
  lastPulseScan: {
    id: 'PrioritiesContent.lastPulseScan',
    defaultMessage: 'Last Pulse scan: {time}',
  },
  neverPulseScan: {
    id: 'PrioritiesContent.neverPulseScan',
    // Pulse scan records are retained for 7 days (ZORI_PULSE_TASK_EXPIRATION_DELTA
    // in the agents service), so an empty lookup means "none in the retention
    // window", not "never ran". Avoid claiming "never".
    defaultMessage: 'No Pulse scan in the last 7 days',
  },
  noDetectedIssues: {
    id: 'PrioritiesContent.noDetectedIssues',
    defaultMessage: 'No detected issues to review.',
  },
  citationsTitle: {
    id: 'PrioritiesContent.citationsTitle',
    defaultMessage: 'Citations',
  },
  suggestedActionsTitle: {
    id: 'PrioritiesContent.suggestedActionsTitle',
    defaultMessage: 'Suggested actions',
  },
  manualActionHint: {
    id: 'PrioritiesContent.manualActionHint',
    defaultMessage: 'Handle this outside of Zori',
  },
  issueDetailsToggle: {
    id: 'PrioritiesContent.issueDetailsToggle',
    defaultMessage: 'Details',
  },
  acknowledgeButton: {
    id: 'PrioritiesContent.acknowledgeButton',
    defaultMessage: 'Acknowledge',
  },
  dismissButton: {
    id: 'PrioritiesContent.dismissButton',
    defaultMessage: 'Dismiss',
  },
  acknowledgeSuccess: {
    id: 'PrioritiesContent.acknowledgeSuccess',
    defaultMessage: 'Issue marked as handled.',
  },
  dismissSuccess: {
    id: 'PrioritiesContent.dismissSuccess',
    defaultMessage: 'Issue dismissed. Thanks for the feedback.',
  },
  dispositionError: {
    id: 'PrioritiesContent.dispositionError',
    defaultMessage: 'Could not update this issue. Please try again.',
  },
  dismissModalTitle: {
    id: 'PrioritiesContent.dismissModalTitle',
    defaultMessage: 'Dismiss this issue',
  },
  dismissModalPrompt: {
    id: 'PrioritiesContent.dismissModalPrompt',
    defaultMessage: 'Why is this issue not useful?',
  },
  acknowledgeModalTitle: {
    id: 'PrioritiesContent.acknowledgeModalTitle',
    defaultMessage: 'Acknowledge this issue',
  },
  acknowledgeModalPrompt: {
    id: 'PrioritiesContent.acknowledgeModalPrompt',
    defaultMessage: 'Share feedback on this issue (optional)',
  },
  acknowledgeConfirm: {
    id: 'PrioritiesContent.acknowledgeConfirm',
    defaultMessage: 'Acknowledge issue',
  },
  dismissReasonNotRelevant: {
    id: 'PrioritiesContent.dismissReasonNotRelevant',
    defaultMessage: 'Not clinically relevant',
  },
  dismissReasonInaccurate: {
    id: 'PrioritiesContent.dismissReasonInaccurate',
    defaultMessage: 'Inaccurate or wrong',
  },
  dismissReasonAlreadyKnown: {
    id: 'PrioritiesContent.dismissReasonAlreadyKnown',
    defaultMessage: 'Already known',
  },
  dismissReasonDuplicate: {
    id: 'PrioritiesContent.dismissReasonDuplicate',
    defaultMessage: 'Duplicate',
  },
  dismissReasonWrongSeverity: {
    id: 'PrioritiesContent.dismissReasonWrongSeverity',
    defaultMessage: 'Wrong severity/urgency',
  },
  dismissReasonOther: {
    id: 'PrioritiesContent.dismissReasonOther',
    defaultMessage: 'Other',
  },
  dismissCommentLabel: {
    id: 'PrioritiesContent.dismissCommentLabel',
    defaultMessage: 'Additional comments (optional)',
  },
  dismissCancel: {
    id: 'PrioritiesContent.dismissCancel',
    defaultMessage: 'Cancel',
  },
  dismissConfirm: {
    id: 'PrioritiesContent.dismissConfirm',
    defaultMessage: 'Dismiss issue',
  },
  sendButton: {
    id: 'PrioritiesContent.sendButton',
    defaultMessage: 'Send',
  },
  sendingButton: {
    id: 'PrioritiesContent.sendingButton',
    defaultMessage: 'Sending…',
  },
  sentButton: {
    id: 'PrioritiesContent.sentButton',
    defaultMessage: 'Sent',
  },
  notificationSent: {
    id: 'PrioritiesContent.notificationSent',
    defaultMessage: 'Notification sent to the member.',
  },
  notificationNotDelivered: {
    id: 'PrioritiesContent.notificationNotDelivered',
    defaultMessage: 'The notification was not delivered to the member.',
  },
  notificationError: {
    id: 'PrioritiesContent.notificationError',
    defaultMessage: 'Could not send the notification. Please try again.',
  },
  careTeamMessageSent: {
    id: 'PrioritiesContent.careTeamMessageSent',
    defaultMessage: 'Message sent to the care team.',
  },
  careTeamMessageError: {
    id: 'PrioritiesContent.careTeamMessageError',
    defaultMessage:
      'Could not send the message to the care team. Please try again.',
  },
  newDataTitle: {
    id: 'PrioritiesContent.newDataTitle',
    defaultMessage: 'New data since {since}',
  },
  newDataDocuments: {
    id: 'PrioritiesContent.newDataDocuments',
    defaultMessage: '{count} {count, plural, one {document} other {documents}}',
  },
  newDataObservations: {
    id: 'PrioritiesContent.newDataObservations',
    defaultMessage:
      '{count} {count, plural, one {observation} other {observations}}',
  },
  newDataCommunications: {
    id: 'PrioritiesContent.newDataCommunications',
    defaultMessage:
      '{count} {count, plural, one {communication} other {communications}}',
  },
  quickLinksTitle: {
    id: 'PrioritiesContent.quickLinksTitle',
    defaultMessage: 'Quick links',
  },
  needsReviewTitle: {
    id: 'PrioritiesContent.needsReviewTitle',
    defaultMessage:
      'Needs review ({count, plural, one {# item} other {# items}})',
  },
  needsReviewItemLabel: {
    id: 'PrioritiesContent.needsReviewItemLabel',
    defaultMessage: 'Review {display}',
  },
  reviewTypeCondition: {
    id: 'PrioritiesContent.reviewTypeCondition',
    defaultMessage: 'Condition',
  },
  reviewTypeObservation: {
    id: 'PrioritiesContent.reviewTypeObservation',
    defaultMessage: 'Lab',
  },
  reviewTypeMedicationStatement: {
    id: 'PrioritiesContent.reviewTypeMedicationStatement',
    defaultMessage: 'Medication',
  },
  reviewTypeAllergyIntolerance: {
    id: 'PrioritiesContent.reviewTypeAllergyIntolerance',
    defaultMessage: 'Allergy',
  },
  reviewTypeImmunization: {
    id: 'PrioritiesContent.reviewTypeImmunization',
    defaultMessage: 'Immunization',
  },
  reviewTypeProcedure: {
    id: 'PrioritiesContent.reviewTypeProcedure',
    defaultMessage: 'Procedure',
  },
});

const REVIEW_TYPE_MESSAGE_IDS: Record<
  ReviewableResourceType,
  keyof typeof messages
> = {
  Condition: 'reviewTypeCondition',
  Observation: 'reviewTypeObservation',
  MedicationStatement: 'reviewTypeMedicationStatement',
  AllergyIntolerance: 'reviewTypeAllergyIntolerance',
  Immunization: 'reviewTypeImmunization',
  Procedure: 'reviewTypeProcedure',
};

// Clinician-facing review priority: Allergies, Conditions, Medications, Labs,
// Procedures, then everything else.
const REVIEW_TYPE_PRIORITY: Record<ReviewableResourceType, number> = {
  AllergyIntolerance: 0,
  Condition: 1,
  MedicationStatement: 2,
  Observation: 3,
  Procedure: 4,
  Immunization: 5,
};

const REVIEW_TYPE_FALLBACK_PRIORITY = Number.MAX_SAFE_INTEGER;

const byReviewPriority = (
  a: UnreviewedExtractedResource,
  b: UnreviewedExtractedResource,
) =>
  (REVIEW_TYPE_PRIORITY[a.resourceType] ?? REVIEW_TYPE_FALLBACK_PRIORITY) -
  (REVIEW_TYPE_PRIORITY[b.resourceType] ?? REVIEW_TYPE_FALLBACK_PRIORITY);

// Warm the source-document cache for the highest-priority needs-review items so
// opening the review modal feels instant.
const REVIEW_PRELOAD_COUNT = 5;

// Suggested actions are read by the member's care team (a Fountain Life
// clinician/health coach), so the vocabulary is framed around what that reviewer
// would do. Most actions the reviewer takes directly; `message_care_team` is the
// exception and loops in another care-team member (e.g. a nurse) via Elation.
// There is intentionally no generic "human review" (the reviewer is already the
// human reviewing it).
type SuggestedAction = {
  actionType:
    | 'send_member_notification'
    | 'message_care_team'
    | 'schedule_follow_up'
    | 'order_or_recommend_test'
    | 'escalate_to_clinician';
  executable: boolean;
  inputs: Record<string, string>;
  rationale: string;
};

type DetectedIssue = {
  id: string;
  severity?: 'high' | 'moderate' | 'low';
  status: string;
  summary: string;
  explanationMarkdown?: string;
  citations?: Array<{ title?: string; reference?: string }>;
  suggestedActions: SuggestedAction[];
};

const SEVERITY_ORDER: Record<string, number> = {
  high: 0,
  moderate: 1,
  low: 2,
};

const sortBySeverity = (a: DetectedIssue, b: DetectedIssue): number => {
  const aOrder = a.severity != null ? SEVERITY_ORDER[a.severity] ?? 3 : 3;
  const bOrder = b.severity != null ? SEVERITY_ORDER[b.severity] ?? 3 : 3;
  return aOrder - bOrder;
};

// Shared between the Dismiss (required) and Acknowledge (optional) feedback
// flows so clinicians pick from the same set of reasons regardless of action.
const FEEDBACK_REASONS: ReadonlyArray<{
  id: string;
  message: keyof typeof messages;
}> = [
  { id: 'not-relevant', message: 'dismissReasonNotRelevant' },
  { id: 'inaccurate', message: 'dismissReasonInaccurate' },
  { id: 'already-known', message: 'dismissReasonAlreadyKnown' },
  { id: 'duplicate', message: 'dismissReasonDuplicate' },
  { id: 'wrong-severity', message: 'dismissReasonWrongSeverity' },
  { id: 'other', message: 'dismissReasonOther' },
];

// Stable key identifying one suggested action on one issue, used to track
// per-action send (in-flight / completed) state across re-renders.
const actionKey = (issueId: string, actionType: string): string =>
  `${issueId}:${actionType}`;

// Fallback member-facing title when the AI proposal omitted one. The body is
// always the (clinician-editable) drafted message.
const DEFAULT_NOTIFICATION_TITLE =
  'A message from your Fountain Life care team';

type SendNotificationParams = {
  issueId: string;
  action: SuggestedAction;
  message: string;
};

const actionTypeLabel = (actionType: SuggestedAction['actionType']): string => {
  switch (actionType) {
    case 'send_member_notification':
      return 'Send notification';
    case 'message_care_team':
      return 'Message care team';
    case 'schedule_follow_up':
      return 'Schedule follow-up';
    case 'order_or_recommend_test':
      return 'Order / recommend test';
    case 'escalate_to_clinician':
      return 'Escalate to clinician';
  }
};

const formatAppointmentTime = (start: string): string =>
  new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(start));

// "Last Pulse scan" timestamp comes from the agents async task record as epoch
// milliseconds; render an absolute local date + time.
const formatScanTimestamp = (createdMs: number): string =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(createdMs));

const formatSinceDate = (since: string): string => {
  const parsed = new Date(since);
  if (Number.isNaN(parsed.getTime())) {
    return since;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
};

const useStyles = makeStyles(theme => ({
  pageContent: {
    flex: 1,
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
  centered: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: theme.spacing(2),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  card: {
    border: `1px solid ${theme.palette.black[100]}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.boxShadows.table,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  cardHeaderIcon: {
    width: 18,
    height: 18,
    color: theme.palette.text.hint,
  },
  contactLabel: {
    color: theme.palette.text.hint,
    fontWeight: theme.typography.fontWeightBold,
  },
  appointmentTime: {
    marginBottom: theme.spacing(0.5),
  },
  appointmentMeta: {
    color: theme.palette.text.hint,
  },
  appointmentLocation: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.hint,
  },
  appointmentLocationIcon: {
    width: 16,
    height: 16,
  },
  reviewHeaderRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  reviewHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  reviewSubtitle: {
    color: theme.palette.text.hint,
  },
  scanSwap: {
    position: 'relative',
    display: 'inline-block',
    width: theme.pxToRem(80),
    height: '1.2em',
    overflow: 'hidden',
    verticalAlign: 'middle',
  },
  scanSwapTrack: {
    display: 'flex',
    width: '200%',
    height: '100%',
    animation: '$scanSwap 4s ease-in-out infinite',
  },
  scanSwapSlot: {
    flex: '0 0 50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '@keyframes scanSwap': {
    '0%, 30%': {
      transform: 'translateX(0)',
    },
    '42%, 88%': {
      transform: 'translateX(-50%)',
    },
    '100%': {
      transform: 'translateX(0)',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    scanSwapTrack: {
      animation: 'none',
      transform: 'translateX(0)',
    },
  },
  newDataRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  needsReviewList: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(1),
    maxHeight: theme.pxToRem(320),
    overflowY: 'auto',
  },
  needsReviewRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    width: '100%',
    padding: theme.spacing(1, 1),
    margin: 0,
    background: 'none',
    border: 'none',
    borderTop: `1px solid ${theme.palette.black[100]}`,
    borderRadius: 6,
    textAlign: 'left',
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    '&:first-child': {
      borderTop: 'none',
    },
    '&:hover, &:focus-visible': {
      backgroundColor: theme.palette.black[50],
    },
  },
  needsReviewInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  needsReviewType: {
    color: theme.palette.text.hint,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  needsReviewDetail: {
    color: theme.palette.text.secondary,
  },
  needsReviewMeta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexShrink: 0,
  },
  citationLink: {
    display: 'inline-block',
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    fontSize: theme.pxToRem(13),
    lineHeight: 1.4,
    color: theme.palette.primary.main,
    textAlign: 'left',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover, &:focus-visible': {
      textDecoration: 'underline',
    },
  },
  issueCard: {
    border: `1px solid ${theme.palette.black[100]}`,
    borderLeftWidth: 4,
    borderLeftStyle: 'solid',
    borderRadius: 6,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  issueDetailsToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: theme.palette.primary.main,
    // Match the "Citations" heading (chroma Text size="subbody").
    fontSize: theme.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  // Render the details body like the card title (subbody) but not bold.
  issueDetailsContent: {
    fontSize: theme.pxToRem(14),
    lineHeight: theme.pxToRem(22),
    fontWeight: theme.typography.fontWeightRegular,
  },
  issueChevron: {
    flexShrink: 0,
    width: 14,
    height: 14,
    transition: 'transform 150ms ease',
  },
  issueChevronOpen: {
    transform: 'rotate(90deg)',
  },
  issueActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.black[100]}`,
  },
  dismissReasonList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  dismissReasonOption: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
  },
  dismissModalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  issueCardHigh: {
    borderLeftColor: theme.palette.red.main,
  },
  issueCardModerate: {
    borderLeftColor: theme.palette.yellow[800],
  },
  issueCardLow: {
    borderLeftColor: theme.palette.black[200],
  },
  actionCard: {
    border: `1px solid ${theme.palette.black[100]}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  messageInput: {
    width: '100%',
    padding: '6px 8px',
    borderRadius: 4,
    border: `1px solid ${theme.palette.black[200]}`,
    marginBottom: theme.spacing(1),
    fontFamily: 'inherit',
    fontSize: 'inherit',
    resize: 'vertical',
    minHeight: 60,
  },
  recommendationItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  recommendationMarker: {
    flexShrink: 0,
    width: 6,
    height: 6,
    marginTop: 7,
    borderRadius: '50%',
    backgroundColor: theme.palette.text.hint,
  },
  recommendationHint: {
    display: 'block',
    marginTop: theme.spacing(0.25),
    color: theme.palette.text.hint,
    fontStyle: 'italic',
  },
  quickLinksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  quickLinkTile: {
    appearance: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    textAlign: 'left',
    textDecoration: 'none',
    cursor: 'pointer',
    width: '100%',
    '&:hover': {
      boxShadow: theme.boxShadows.focusVisible,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  quickLinkIconBadge: {
    alignItems: 'center',
    backgroundColor: alpha(
      theme.palette.mode === 'dark'
        ? theme.palette.blue[200]
        : theme.palette.primary.main,
      theme.palette.mode === 'dark' ? 0.15 : 0.075,
    ),
    borderRadius: '50%',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.blue[200]
        : theme.palette.primary.main,
    display: 'flex',
    flexShrink: 0,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  quickLinkIcon: {
    height: 18,
    width: 18,
  },
  quickLinkLabel: {
    flex: 1,
    minWidth: 0,
    lineHeight: 1.25,
  },
  quickLinkChevron: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    height: 18,
    width: 18,
  },
}));

type SuggestedActionCardProps = {
  action: SuggestedAction;
  issueId: string;
  onSend: (params: SendNotificationParams) => void;
  isSending: boolean;
  isSent: boolean;
};

const SuggestedActionCard: React.FC<SuggestedActionCardProps> = ({
  action,
  issueId,
  onSend,
  isSending,
  isSent,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const [messageText, setMessageText] = useState(action.inputs.message ?? '');

  if (action.executable) {
    const sendDisabled = isSending || isSent || messageText.trim() === '';
    return (
      <div
        className={classes.actionCard}
        data-testid={`suggested-action-${issueId}-${action.actionType}`}
      >
        <Text size="subbody" weight="bold">
          {actionTypeLabel(action.actionType)}
        </Text>
        <Text size="subbody" style={{ marginBottom: 8 }}>
          {action.rationale}
        </Text>
        {action.inputs.message != null ? (
          <textarea
            aria-label={`Message for ${actionTypeLabel(action.actionType)}`}
            className={classes.messageInput}
            value={messageText}
            onChange={e => setMessageText(e.target.value)}
            disabled={isSending || isSent}
            data-testid={`action-message-input-${issueId}-${action.actionType}`}
          />
        ) : null}
        <Button
          variant="contained"
          color="default"
          onClick={() => onSend({ issueId, action, message: messageText })}
          disabled={sendDisabled}
          data-testid={`action-send-${issueId}-${action.actionType}`}
        >
          {formatMessage(
            isSent
              ? messages.sentButton
              : isSending
              ? messages.sendingButton
              : messages.sendButton,
          )}
        </Button>
      </div>
    );
  }

  // Non-executable actions have no API wired up, so the UI can't trigger them.
  // Render them as a read-only recommendation (no checkbox / button) to avoid
  // implying the clinician can action them from here.
  return (
    <div
      className={classes.actionCard}
      data-testid={`suggested-action-${issueId}-${action.actionType}`}
    >
      <div className={classes.recommendationItem}>
        <span className={classes.recommendationMarker} aria-hidden />
        <div>
          <Text size="subbody" weight="bold">
            {actionTypeLabel(action.actionType)}
          </Text>
          <Text size="subbody">{action.rationale}</Text>
          <Text size="caption" className={classes.recommendationHint}>
            {formatMessage(messages.manualActionHint)}
          </Text>
        </div>
      </div>
    </div>
  );
};

type DetectedIssueCardProps = {
  issue: DetectedIssue;
  patientId: string;
  onSendNotification: (params: SendNotificationParams) => void;
  sendingActionKey: string | null;
  sentActionKeys: ReadonlySet<string>;
  onOpenCitation: (reference: ParsedCitationReference) => void;
  onOpenChatMessage?: (messageId: string) => void;
  onAcknowledge: (issue: DetectedIssue) => void;
  onDismiss: (issue: DetectedIssue) => void;
  isBusy: boolean;
};

const DetectedIssueCard: React.FC<DetectedIssueCardProps> = ({
  issue,
  patientId,
  onSendNotification,
  sendingActionKey,
  sentActionKeys,
  onOpenCitation,
  onOpenChatMessage,
  onAcknowledge,
  onDismiss,
  isBusy,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  // Only the explanation body text collapses (collapsed by default) to cut
  // visual noise; the title, citations, suggested actions, and the
  // Acknowledge/Dismiss actions all stay visible so the list stays scannable.
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const severityClass =
    issue.severity === 'high'
      ? classes.issueCardHigh
      : issue.severity === 'moderate'
      ? classes.issueCardModerate
      : classes.issueCardLow;

  return (
    <div
      className={`${classes.issueCard} ${severityClass}`}
      data-testid={`issue-card-${issue.id}`}
    >
      <Box direction="row" justify="space-between" gap={1} marginBottom={1}>
        <Text
          size="subbody"
          weight="bold"
          data-testid={`issue-summary-${issue.id}`}
        >
          {issue.summary}
        </Text>
        <Box direction="row" gap={1}>
          {issue.severity != null && (
            <Pill
              label={issue.severity}
              data-testid={`issue-severity-${issue.id}`}
            />
          )}
          <Pill label={issue.status} data-testid={`issue-status-${issue.id}`} />
        </Box>
      </Box>

      {issue.explanationMarkdown != null && (
        <div style={{ marginBottom: 8 }}>
          <button
            type="button"
            className={classes.issueDetailsToggle}
            onClick={() => setDetailsExpanded(current => !current)}
            aria-expanded={detailsExpanded}
            data-testid={`issue-details-toggle-${issue.id}`}
          >
            <ChevronRight
              className={`${classes.issueChevron} ${
                detailsExpanded ? classes.issueChevronOpen : ''
              }`}
              aria-hidden
            />
            {formatMessage(messages.issueDetailsToggle)}
          </button>
          {detailsExpanded && (
            <div
              style={{ marginTop: 4 }}
              data-testid={`issue-details-${issue.id}`}
            >
              <LexicalReadOnlyMarkdownViewer
                className={classes.issueDetailsContent}
                markdown={issue.explanationMarkdown}
                namespace={`priorities-issue-explanation-${issue.id}`}
                syncKey={issue.id}
              />
            </div>
          )}
        </div>
      )}

      {issue.citations != null && issue.citations.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Text size="subbody" weight="bold">
            {formatMessage(messages.citationsTitle)}
          </Text>
          {issue.citations.map((citation, idx) => {
            const parsedReference = parseCitationReference(citation.reference);
            if (parsedReference != null) {
              return (
                <div key={citation.reference ?? idx}>
                  <button
                    type="button"
                    className={classes.citationLink}
                    onClick={() => onOpenCitation(parsedReference)}
                    data-testid={`citation-${issue.id}-${idx}`}
                  >
                    {citation.title ?? citation.reference}
                  </button>
                </div>
              );
            }
            // Conversation-review citations point at a member-chat message by
            // its post id; surface them as links that open the member chat and
            // scroll/highlight that message instead of showing the raw id.
            const chatMessageId = parseChatMessageReference(citation.reference);
            if (chatMessageId != null && onOpenChatMessage != null) {
              return (
                <div key={citation.reference ?? idx}>
                  <button
                    type="button"
                    className={classes.citationLink}
                    onClick={() => onOpenChatMessage(chatMessageId)}
                    data-testid={`citation-${issue.id}-${idx}`}
                  >
                    {citation.title ?? citation.reference}
                  </button>
                </div>
              );
            }
            return (
              <Text
                key={citation.reference ?? idx}
                size="subbody"
                data-testid={`citation-${issue.id}-${idx}`}
              >
                {citation.title
                  ? `${citation.title}${
                      citation.reference ? ` (${citation.reference})` : ''
                    }`
                  : citation.reference ?? '—'}
              </Text>
            );
          })}
        </div>
      )}

      {issue.suggestedActions.length > 0 && (
        <div>
          <Text size="subbody" weight="bold" style={{ marginBottom: 6 }}>
            {formatMessage(messages.suggestedActionsTitle)}
          </Text>
          {issue.suggestedActions.map(action => (
            <SuggestedActionCard
              key={action.actionType}
              action={action}
              issueId={issue.id}
              onSend={onSendNotification}
              isSending={
                sendingActionKey === actionKey(issue.id, action.actionType)
              }
              isSent={sentActionKeys.has(
                actionKey(issue.id, action.actionType),
              )}
            />
          ))}
        </div>
      )}

      <ActionToolkit patientId={patientId} issueId={issue.id} />

      <div className={classes.issueActions}>
        <Button
          variant="contained"
          color="default"
          onClick={() => onAcknowledge(issue)}
          disabled={isBusy}
          data-testid={`issue-acknowledge-${issue.id}`}
        >
          {formatMessage(messages.acknowledgeButton)}
        </Button>
        <Button
          variant="outlined"
          onClick={() => onDismiss(issue)}
          disabled={isBusy}
          data-testid={`issue-dismiss-${issue.id}`}
        >
          {formatMessage(messages.dismissButton)}
        </Button>
      </div>
    </div>
  );
};

type IssueFeedback = { reason: string; comment?: string };

type IssueFeedbackModalProps = {
  issue: DetectedIssue;
  // 'handled' (Acknowledge) collects optional feedback; 'not-useful'
  // (Dismiss) requires a reason before the clinician can submit.
  disposition: 'handled' | 'not-useful';
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (feedback?: IssueFeedback) => void;
};

const IssueFeedbackModal: React.FC<IssueFeedbackModalProps> = ({
  issue,
  disposition,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const [reason, setReason] = useState<string>('');
  const [comment, setComment] = useState<string>('');

  const isDismiss = disposition === 'not-useful';
  // Acknowledge feedback is optional, so the same reason list doubles as an
  // opt-in note; Dismiss keeps the reason mandatory.
  const testIdPrefix = isDismiss ? 'dismiss' : 'acknowledge';

  return (
    <Modal
      isOpen
      size={1}
      fullWidth
      onDismiss={onCancel}
      title={formatMessage(
        isDismiss ? messages.dismissModalTitle : messages.acknowledgeModalTitle,
      )}
      data-testid={`${testIdPrefix}-feedback-modal`}
    >
      <Box padding={2} direction="column">
        <Text
          size="subbody"
          weight="bold"
          data-testid={`${testIdPrefix}-issue-summary`}
        >
          {issue.summary}
        </Text>
        <Text size="subbody" style={{ marginTop: 8 }}>
          {formatMessage(
            isDismiss
              ? messages.dismissModalPrompt
              : messages.acknowledgeModalPrompt,
          )}
        </Text>
        <div className={classes.dismissReasonList} role="radiogroup">
          {FEEDBACK_REASONS.map(option => (
            <label key={option.id} className={classes.dismissReasonOption}>
              <input
                type="radio"
                name={`${testIdPrefix}-reason`}
                value={option.id}
                checked={reason === option.id}
                onChange={() => setReason(option.id)}
                data-testid={`${testIdPrefix}-reason-${option.id}`}
              />
              <Text size="subbody">
                {formatMessage(messages[option.message])}
              </Text>
            </label>
          ))}
        </div>
        <textarea
          aria-label={formatMessage(messages.dismissCommentLabel)}
          placeholder={formatMessage(messages.dismissCommentLabel)}
          className={classes.messageInput}
          value={comment}
          onChange={event => setComment(event.target.value)}
          data-testid={`${testIdPrefix}-comment`}
        />
        <div className={classes.dismissModalActions}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid={`${testIdPrefix}-cancel`}
          >
            {formatMessage(messages.dismissCancel)}
          </Button>
          <Button
            variant="contained"
            color="default"
            // Dismiss needs a reason; Acknowledge can be confirmed with no
            // feedback at all.
            disabled={(isDismiss && reason === '') || isSubmitting}
            onClick={() => {
              const trimmedComment =
                comment.trim() === '' ? undefined : comment.trim();
              // No reason picked (only possible for Acknowledge) means no
              // feedback to record. A free-text comment is only persisted
              // alongside a reason since the API requires one.
              onSubmit(
                reason === '' ? undefined : { reason, comment: trimmedComment },
              );
            }}
            data-testid={`${testIdPrefix}-submit`}
          >
            {formatMessage(
              isDismiss ? messages.dismissConfirm : messages.acknowledgeConfirm,
            )}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

type NextAppointmentCardProps = {
  appointment: NextAppointmentSummary | null;
  isLoading: boolean;
};

const NextAppointmentCard: React.FC<NextAppointmentCardProps> = ({
  appointment,
  isLoading,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  const locationType = appointment?.locationType;
  const locationLabel =
    locationType === 'in-person'
      ? formatMessage(messages.inPerson)
      : locationType === 'virtual'
      ? formatMessage(messages.virtual)
      : null;

  return (
    <div className={classes.card} data-testid="appointments-card">
      <div className={classes.cardHeader}>
        <Calendar className={classes.cardHeaderIcon} />
        <Text size="caption" weight="bold" className={classes.contactLabel}>
          {formatMessage(
            appointment?.isPast
              ? messages.lastAppointmentTitle
              : messages.nextAppointmentTitle,
          )}
        </Text>
      </div>
      {isLoading ? (
        <PulseLoader width={40} />
      ) : appointment?.start ? (
        <Box data-testid="appointment-0">
          <Text size="body" weight="bold" className={classes.appointmentTime}>
            {formatAppointmentTime(appointment.start)}
          </Text>
          {appointment.type != null ? (
            <Text size="subbody">{appointment.type}</Text>
          ) : null}
          {appointment.practitionerName != null ? (
            <Text size="subbody" className={classes.appointmentMeta}>
              {formatMessage(messages.practitionerLabel, {
                name: appointment.practitionerName,
              })}
            </Text>
          ) : null}
          {locationLabel ? (
            <div className={classes.appointmentLocation}>
              {locationType === 'virtual' ? (
                <Video2 className={classes.appointmentLocationIcon} />
              ) : (
                <MapPin className={classes.appointmentLocationIcon} />
              )}
              <Text size="subbody" className={classes.appointmentMeta}>
                {locationLabel}
              </Text>
            </div>
          ) : null}
        </Box>
      ) : (
        <Text size="subbody" className={classes.appointmentMeta}>
          {formatMessage(messages.noUpcomingAppointments)}
        </Text>
      )}
    </div>
  );
};

export type PrioritiesContentProps = {
  memberId: string;
  // Project the Zori Pulse scan runs against; required to invoke a new scan.
  projectId: string;
  fallback: React.ReactNode;
  onOpenZoriChat?: () => void;
  onOpenChatMessage?: (messageId: string) => void;
  canZoriPulseScan?: boolean;
};

export const PrioritiesContent: React.FC<PrioritiesContentProps> = ({
  memberId,
  projectId,
  fallback,
  onOpenZoriChat,
  onOpenChatMessage,
  canZoriPulseScan,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const [notify] = useNotify();

  const query = apiHooks.useAPIQuery(
    'GET /v1/member-operations/priorities/members/:patientId',
    { patientId: memberId },
    { staleTime: 0, refetchOnWindowFocus: false },
  );

  // Zori Pulse scan controller. Owning the scan state here (rather than in the
  // shared MemberDetail parent) keeps its poll loop from re-rendering other
  // member sections: the hook only runs while the Pulse section is mounted. It
  // surfaces the last completed run, the running state, and the invoke action,
  // and refreshes the last-run line once a scan completes.
  const {
    lastCompletedScanAt,
    isLoadingLastRun,
    isRunning: isZoriPulseScanRunning,
    triggerScan,
  } = useMemberPulseScan({
    patientId: memberId,
    projectId,
    enabled: Boolean(canZoriPulseScan),
  });

  const { appointment: nextAppointment, isLoading: isNextAppointmentLoading } =
    useNextAppointment(memberId);

  const { resources: needsReviewResources } =
    useUnreviewedExtractedResources(memberId);

  const preloadReviewItems = useMemo(
    () =>
      [...needsReviewResources]
        .sort(byReviewPriority)
        .slice(0, REVIEW_PRELOAD_COUNT),
    [needsReviewResources],
  );
  useExtractedResourceSourcePreload(preloadReviewItems);

  const [selectedReviewItem, setSelectedReviewItem] =
    useState<UnreviewedExtractedResource | null>(null);
  const [selectedCitation, setSelectedCitation] =
    useState<ParsedCitationReference | null>(null);
  const [resolvedReviewKeys, setResolvedReviewKeys] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [dispositionedIssueIds, setDispositionedIssueIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [feedbackTarget, setFeedbackTarget] = useState<{
    issue: DetectedIssue;
    disposition: 'handled' | 'not-useful';
  } | null>(null);
  const [pendingIssueId, setPendingIssueId] = useState<string | null>(null);
  const [sendingActionKey, setSendingActionKey] = useState<string | null>(null);
  const [sentActionKeys, setSentActionKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const dispositionMutation = apiHooks.useAPIMutation(
    'POST /v1/member-operations/priorities/members/:patientId/detected-issues/:issueId/disposition',
  );

  const sendNotificationMutation = apiHooks.useAPIMutation(
    'POST /v1/member-operations/priorities/members/:patientId/detected-issues/:issueId/actions/send-member-notification',
  );

  const sendCareTeamMessageMutation = apiHooks.useAPIMutation(
    'POST /v1/member-operations/priorities/members/:patientId/actions/message-care-team',
  );

  const setIssueHidden = (issueId: string, hidden: boolean) => {
    setDispositionedIssueIds(current => {
      const next = new Set(current);
      if (hidden) {
        next.add(issueId);
      } else {
        next.delete(issueId);
      }
      return next;
    });
  };

  // Optimistically hide the card, then reconcile on success/error. Both paths
  // keep the underlying resource so the AI scans won't regenerate the issue.
  const applyDisposition = (
    issue: DetectedIssue,
    disposition: 'handled' | 'not-useful',
    feedback?: IssueFeedback,
  ) => {
    setPendingIssueId(issue.id);
    setIssueHidden(issue.id, true);
    dispositionMutation.mutate(
      { patientId: memberId, issueId: issue.id, disposition, feedback },
      {
        onSuccess: () => {
          notify(
            formatMessage(
              disposition === 'handled'
                ? messages.acknowledgeSuccess
                : messages.dismissSuccess,
            ),
            'success',
          );
          void query.refetch();
        },
        onError: () => {
          setIssueHidden(issue.id, false);
          notify(formatMessage(messages.dispositionError), 'error');
        },
        onSettled: () => setPendingIssueId(null),
      },
    );
  };

  // Acknowledge and Dismiss both route through the feedback modal; Acknowledge
  // makes the feedback optional while Dismiss requires a reason.
  const handleAcknowledgeIssue = (issue: DetectedIssue) =>
    setFeedbackTarget({ issue, disposition: 'handled' });

  const handleDismissIssue = (issue: DetectedIssue) =>
    setFeedbackTarget({ issue, disposition: 'not-useful' });

  const handleFeedbackSubmit = (feedback?: IssueFeedback) => {
    if (feedbackTarget == null) {
      return;
    }
    applyDisposition(
      feedbackTarget.issue,
      feedbackTarget.disposition,
      feedback,
    );
    setFeedbackTarget(null);
  };

  const reviewKey = (item: { resourceType: string; id: string }) =>
    `${item.resourceType}/${item.id}`;

  // Mark a single extracted resource as reviewed (approved/rejected) and remove
  // it from the card. The modal stays open until all queued siblings are done.
  const markReviewResolved = (item: { resourceType: string; id: string }) => {
    setResolvedReviewKeys(current => {
      const next = new Set(current);
      next.add(reviewKey(item));
      return next;
    });
  };

  const typeLabelFor = (resourceType: ReviewableResourceType) =>
    formatMessage(messages[REVIEW_TYPE_MESSAGE_IDS[resourceType]]);

  // Execute a `send_member_notification` suggested action: send the member the
  // (clinician-editable) drafted message via member-operations-service, which
  // fans out to member-notification-service. We surface partial outcomes
  // (suppressed / undelivered) rather than reporting a blanket success.
  const handleSendNotification = ({
    issueId,
    action,
    message,
  }: SendNotificationParams) => {
    const key = actionKey(issueId, action.actionType);

    // `message_care_team` loops in the member's care team via Elation rather
    // than notifying the member, so it routes to the care-team messaging
    // endpoint instead of member-notification.
    if (action.actionType === 'message_care_team') {
      setSendingActionKey(key);
      sendCareTeamMessageMutation.mutate(
        { patientId: memberId, issueId, message },
        {
          onSuccess: () => {
            setSentActionKeys(current => new Set(current).add(key));
            notify(formatMessage(messages.careTeamMessageSent), 'success');
          },
          onError: () =>
            notify(formatMessage(messages.careTeamMessageError), 'error'),
          onSettled: () => setSendingActionKey(null),
        },
      );
      return;
    }

    setSendingActionKey(key);
    sendNotificationMutation.mutate(
      {
        patientId: memberId,
        issueId,
        title: action.inputs.title ?? DEFAULT_NOTIFICATION_TITLE,
        message,
      },
      {
        onSuccess: data => {
          if (data.errors != null && data.errors.length > 0) {
            notify(formatMessage(messages.notificationError), 'error');
            return;
          }
          if (!data.emailSent && !data.smsSent) {
            notify(
              data.info?.[0] ??
                formatMessage(messages.notificationNotDelivered),
              'info',
            );
            return;
          }
          setSentActionKeys(current => new Set(current).add(key));
          notify(formatMessage(messages.notificationSent), 'success');
        },
        onError: () =>
          notify(formatMessage(messages.notificationError), 'error'),
        onSettled: () => setSendingActionKey(null),
      },
    );
  };

  if (query.isLoading) {
    return (
      <div className={classes.centered} data-testid="priorities-loading">
        <Box direction="column" align="center" gap={1}>
          <PulseLoader width={96} />
          <Text size="subbody">{formatMessage(messages.loading)}</Text>
        </Box>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={classes.centered} data-testid="priorities-error">
        <Text size="subbody">{formatMessage(messages.error)}</Text>
      </div>
    );
  }

  if (!query.data) {
    return <>{fallback}</>;
  }

  const { detectedIssues, newData } = query.data;
  const sortedIssues = [...detectedIssues]
    .filter(issue => !dispositionedIssueIds.has(issue.id))
    .sort(sortBySeverity);
  const visibleNeedsReview = needsReviewResources
    .filter(item => !resolvedReviewKeys.has(reviewKey(item)))
    .sort(byReviewPriority);
  const hasNothingToReview =
    sortedIssues.length === 0 && !newData && visibleNeedsReview.length === 0;

  return (
    <div className={classes.pageContent} data-testid="priorities-content">
      <div className={classes.layout}>
        {/* Left column: next appointment, new data, needs review, quick links */}
        <Box className={classes.column}>
          <NextAppointmentCard
            appointment={nextAppointment}
            isLoading={isNextAppointmentLoading}
          />

          {newData != null && (
            <div className={classes.card} data-testid="new-data-card">
              <Text
                size="caption"
                weight="bold"
                className={classes.contactLabel}
              >
                {formatMessage(messages.newDataTitle, {
                  since: formatSinceDate(newData.since),
                })}
              </Text>
              <div className={classes.newDataRow}>
                {newData.documents > 0 && (
                  <Pill
                    label={formatMessage(messages.newDataDocuments, {
                      count: newData.documents,
                    })}
                    data-testid="new-data-documents"
                  />
                )}
                {newData.observations > 0 && (
                  <Pill
                    label={formatMessage(messages.newDataObservations, {
                      count: newData.observations,
                    })}
                    data-testid="new-data-observations"
                  />
                )}
                {newData.communications > 0 && (
                  <Pill
                    label={formatMessage(messages.newDataCommunications, {
                      count: newData.communications,
                    })}
                    data-testid="new-data-communications"
                  />
                )}
              </div>
            </div>
          )}

          {visibleNeedsReview.length > 0 && (
            <div className={classes.card} data-testid="needs-review-card">
              <Text
                size="caption"
                weight="bold"
                className={classes.contactLabel}
              >
                {formatMessage(messages.needsReviewTitle, {
                  count: visibleNeedsReview.length,
                })}
              </Text>
              <div className={classes.needsReviewList}>
                {visibleNeedsReview.map(item => {
                  const value = getExtractedObservationValue(item.resource);
                  const date = getExtractedResourceDate(item.resource);
                  const detail = [
                    value,
                    date ? formatSinceDate(date) : undefined,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <div
                      role="button"
                      tabIndex={0}
                      key={`${item.resourceType}-${item.id}`}
                      className={classes.needsReviewRow}
                      data-testid={`needs-review-${item.resourceType}-${item.id}`}
                      onClick={() => setSelectedReviewItem(item)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedReviewItem(item);
                        }
                      }}
                      aria-label={formatMessage(messages.needsReviewItemLabel, {
                        display: item.display,
                      })}
                    >
                      <div className={classes.needsReviewInfo}>
                        <Text size="subbody" weight="bold">
                          {item.display}
                        </Text>
                        <Text
                          size="caption"
                          className={classes.needsReviewType}
                        >
                          {formatMessage(
                            messages[
                              REVIEW_TYPE_MESSAGE_IDS[item.resourceType]
                            ],
                          )}
                        </Text>
                        {detail ? (
                          <Text
                            size="caption"
                            className={classes.needsReviewDetail}
                            data-testid={`needs-review-detail-${item.resourceType}-${item.id}`}
                          >
                            {detail}
                          </Text>
                        ) : null}
                      </div>
                      <div className={classes.needsReviewMeta}>
                        <ExtractionConfidenceBadge resource={item.resource} />
                        <DocumentExtractionReviewStatus
                          resource={item.resource}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className={classes.card} data-testid="quick-links-section">
            <Text size="caption" weight="bold" className={classes.contactLabel}>
              {formatMessage(messages.quickLinksTitle)}
            </Text>
            <div className={classes.quickLinksList}>
              {(
                [
                  {
                    testId: 'quick-link-annual-report',
                    href: `${urls.app.members(memberId)}/annual-report`,
                    label: formatMessage(
                      memberDetailMessages.annualReportLabel,
                    ),
                    icon: FileText,
                  },
                  {
                    testId: 'quick-link-zori-reports',
                    href: `${urls.app.members(memberId)}/ai-tools`,
                    label: formatMessage(memberDetailMessages.aiToolsLabel),
                    icon: SparkleFile,
                  },
                  {
                    testId: 'quick-link-labs',
                    href: `${urls.app.members(memberId)}/labs`,
                    label: formatMessage(memberDetailMessages.labsLabel),
                    icon: Beaker,
                  },
                  {
                    testId: 'quick-link-zori-chat',
                    onClick: onOpenZoriChat,
                    label: formatMessage(memberDetailMessages.zoriChatLabel),
                    icon: SparkleCircle,
                  },
                ] as const
              ).map(link => {
                const IconComponent = link.icon;
                const inner = (
                  <>
                    <span className={classes.quickLinkIconBadge}>
                      <IconComponent className={classes.quickLinkIcon} />
                    </span>
                    <Text
                      size="subbody"
                      weight="bold"
                      className={classes.quickLinkLabel}
                    >
                      {link.label}
                    </Text>
                    <ChevronRight className={classes.quickLinkChevron} />
                  </>
                );

                return 'href' in link ? (
                  <Link
                    key={link.testId}
                    to={link.href}
                    data-testid={link.testId}
                    className={classes.quickLinkTile}
                  >
                    {inner}
                  </Link>
                ) : (
                  <button
                    key={link.testId}
                    type="button"
                    onClick={link.onClick}
                    data-testid={link.testId}
                    className={classes.quickLinkTile}
                  >
                    {inner}
                  </button>
                );
              })}
            </div>
          </div>
        </Box>

        {/* Right column: review heading + detected issues */}
        <Box className={classes.column} data-testid="detected-issues-section">
          <div className={classes.reviewHeaderRow}>
            <div className={classes.reviewHeader}>
              <Text weight="bold">{formatMessage(messages.reviewTitle)}</Text>
              <Text size="caption" className={classes.reviewSubtitle}>
                {formatMessage(messages.reviewSubtitle, {
                  count: sortedIssues.length,
                })}
              </Text>
              {canZoriPulseScan && !isLoadingLastRun && (
                <Text
                  size="caption"
                  className={classes.reviewSubtitle}
                  data-testid="last-pulse-scan"
                >
                  {lastCompletedScanAt != null
                    ? formatMessage(messages.lastPulseScan, {
                        time: formatScanTimestamp(lastCompletedScanAt),
                      })
                    : formatMessage(messages.neverPulseScan)}
                </Text>
              )}
            </div>
            {canZoriPulseScan ? (
              <Button
                variant="outlined"
                icon={Activity}
                onClick={() => void triggerScan()}
                disabled={isZoriPulseScanRunning}
                data-testid="priorities-zori-pulse-scan"
                aria-label={
                  isZoriPulseScanRunning
                    ? formatMessage(memberDetailMessages.zoriPulseScanRunning)
                    : undefined
                }
              >
                {isZoriPulseScanRunning ? (
                  <span className={classes.scanSwap} aria-hidden>
                    <span className={classes.scanSwapTrack}>
                      <span className={classes.scanSwapSlot}>
                        {formatMessage(
                          memberDetailMessages.zoriPulseScanRunning,
                        )}
                      </span>
                      <span className={classes.scanSwapSlot}>
                        <PulseLoader width={56} speed={1.4} />
                      </span>
                    </span>
                  </span>
                ) : (
                  formatMessage(memberDetailMessages.zoriPulseScanButton)
                )}
              </Button>
            ) : null}
          </div>

          {hasNothingToReview ? (
            <div className={classes.card} data-testid="priorities-empty">
              <Text size="subbody" className={classes.appointmentMeta}>
                {formatMessage(messages.noDetectedIssues)}
              </Text>
            </div>
          ) : sortedIssues.length === 0 ? (
            <div className={classes.card}>
              <Text size="subbody" className={classes.appointmentMeta}>
                {formatMessage(messages.noDetectedIssues)}
              </Text>
            </div>
          ) : (
            sortedIssues.map(issue => (
              <DetectedIssueCard
                key={issue.id}
                issue={issue}
                patientId={memberId}
                onSendNotification={handleSendNotification}
                sendingActionKey={sendingActionKey}
                sentActionKeys={sentActionKeys}
                onOpenCitation={setSelectedCitation}
                onOpenChatMessage={onOpenChatMessage}
                onAcknowledge={handleAcknowledgeIssue}
                onDismiss={handleDismissIssue}
                isBusy={pendingIssueId === issue.id}
              />
            ))
          )}
        </Box>
      </div>

      {selectedReviewItem && (
        <ExtractedResourceReviewModal
          items={visibleNeedsReview}
          selected={selectedReviewItem}
          subjectId={memberId}
          typeLabelFor={typeLabelFor}
          onResolveItem={markReviewResolved}
          onDismiss={() => setSelectedReviewItem(null)}
        />
      )}

      {selectedCitation && (
        <CitationResourceModal
          key={
            selectedCitation.kind === 'medicalResult'
              ? `medicalResult/${selectedCitation.resourceId}`
              : `${selectedCitation.resourceType}/${selectedCitation.id}`
          }
          reference={selectedCitation}
          subjectId={memberId}
          onDismiss={() => setSelectedCitation(null)}
        />
      )}

      {feedbackTarget && (
        <IssueFeedbackModal
          key={feedbackTarget.issue.id}
          issue={feedbackTarget.issue}
          disposition={feedbackTarget.disposition}
          isSubmitting={pendingIssueId === feedbackTarget.issue.id}
          onCancel={() => setFeedbackTarget(null)}
          onSubmit={handleFeedbackSubmit}
        />
      )}
    </div>
  );
};
